
import * as React from 'react';
import { createRoot } from 'react-dom/client';
import './styles.css';
import type { MiniAppSandboxTelemetryPayload } from './services/miniAppSandboxTelemetry';
import { FrontendRuntimeConfigError, initializeFrontendRuntimeConfig } from './services/frontendRuntimeConfig';

const PRELOAD_RELOAD_MARKER_KEY = 'plato:vite-preload-reload';
const STYLESHEET_RELOAD_MARKER_KEY = 'plato:stylesheet-reload';
const RELOAD_GUARD_WINDOW_MS = 10000;
const MINI_APP_SANDBOX_TELEMETRY_EVENT = 'plato:mini-app-sandbox-telemetry';

type ReloadMarker = {
  message: string;
  timestamp: number;
};

const readReloadMarker = (storageKey: string): ReloadMarker | null => {
  try {
    const rawMarker = globalThis.window?.sessionStorage.getItem(storageKey);

    if (!rawMarker) {
      return null;
    }

    const marker = JSON.parse(rawMarker) as ReloadMarker;
    if (typeof marker.message !== 'string' || typeof marker.timestamp !== 'number') {
      return null;
    }

    return marker;
  } catch {
    return null;
  }
};

const writeReloadMarker = (storageKey: string, marker: ReloadMarker) => {
  try {
    globalThis.window?.sessionStorage.setItem(storageKey, JSON.stringify(marker));
  } catch {
    // Ignore storage failures and fall back to a normal reload.
  }
};

const clearReloadMarker = (storageKey: string) => {
  try {
    globalThis.window?.sessionStorage.removeItem(storageKey);
  } catch {
    // Ignore storage failures.
  }
};

const scheduleSingleReload = (storageKey: string, message: string) => {
  const previousMarker = readReloadMarker(storageKey);
  const now = Date.now();
  const isRepeatedImmediateFailure = previousMarker?.message === message
    && now - previousMarker.timestamp < RELOAD_GUARD_WINDOW_MS;

  if (isRepeatedImmediateFailure) {
    return;
  }

  writeReloadMarker(storageKey, { message, timestamp: now });
  globalThis.window?.location.reload();
};

const getPreloadErrorMessage = (event: Event) => {
  const customEvent = event as CustomEvent<{ path?: string; message?: string; error?: unknown }> & {
    payload?: { path?: string; message?: string; error?: unknown } | Error;
  };
  const payload = customEvent.payload;

  if (payload instanceof Error) {
    return payload.message;
  }

  if (typeof payload?.message === 'string' && payload.message.length > 0) {
    return payload.message;
  }

  if (payload?.error instanceof Error) {
    return payload.error.message;
  }

  if (typeof payload?.path === 'string' && payload.path.length > 0) {
    return payload.path;
  }

  return 'vite:preloadError';
};

const isMiniAppSandboxTelemetryPayload = (value: unknown): value is MiniAppSandboxTelemetryPayload => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const payload = value as Record<string, unknown>;
  return payload.runtimeMode === 'react_sandbox'
    && typeof payload.eventName === 'string'
    && typeof payload.solutionId === 'string'
    && typeof payload.appId === 'string'
    && typeof payload.timestamp === 'string';
};

const createMiniAppSandboxTelemetryHandler = (
  resolveAppEntryMode: () => { isStandaloneMiniAppMode: boolean },
) => (event: Event) => {
    const payload = (event as CustomEvent<MiniAppSandboxTelemetryPayload>).detail;

    if (!isMiniAppSandboxTelemetryPayload(payload) || resolveAppEntryMode().isStandaloneMiniAppMode) {
      return;
    }

    void import('./services/miniAppSandboxTelemetryIngestionService').then(({ reportMiniAppSandboxTelemetry }) => (
      payload.authMode === 'scoped-session'
        ? reportMiniAppSandboxTelemetry(payload.solutionId, payload.appId, payload, { authMode: 'scoped-session' })
        : reportMiniAppSandboxTelemetry(payload.solutionId, payload.appId, payload)
    )).catch(error => {
      console.error('Failed to report Mini-App sandbox telemetry', error);
    });
  };

const hasBaseStylesLoaded = () => {
  const body = globalThis.window?.document?.body;

  if (!body || !globalThis.window) {
    return true;
  }

  const computedBodyStyle = globalThis.window.getComputedStyle(body);
  return computedBodyStyle.overflow === 'hidden';
};

globalThis.window?.addEventListener('vite:preloadError', event => {
  event.preventDefault();

  const message = getPreloadErrorMessage(event);
  scheduleSingleReload(PRELOAD_RELOAD_MARKER_KEY, message);
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = createRoot(rootElement);

const renderDeploymentError = (code: string) => {
  root.render(
    <main role="alert" className="flex min-h-screen items-center justify-center bg-slate-950 px-6 text-slate-100">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-semibold">Deployment unavailable</h1>
        <p className="mt-3 text-sm text-slate-300">Runtime configuration validation failed.</p>
        <p className="mt-2 font-mono text-xs text-slate-400">{code}</p>
      </div>
    </main>,
  );
};

export const frontendBootstrapPromise = (async () => {
  try {
    await initializeFrontendRuntimeConfig();
    const [appModule, appRuntimeEntryModule] = await Promise.all([
      import('./App'),
      import('./services/appRuntimeEntry'),
    ]);
    const App = appModule.default;
    const handleMiniAppSandboxTelemetry = createMiniAppSandboxTelemetryHandler(appRuntimeEntryModule.resolveAppEntryMode);
    globalThis.window?.addEventListener(MINI_APP_SANDBOX_TELEMETRY_EVENT, handleMiniAppSandboxTelemetry as EventListener);

    function Root() {
      React.useEffect(() => {
        const frameHandle = globalThis.window?.requestAnimationFrame(() => {
          if (hasBaseStylesLoaded()) {
            clearReloadMarker(PRELOAD_RELOAD_MARKER_KEY);
            clearReloadMarker(STYLESHEET_RELOAD_MARKER_KEY);
            return;
          }

          scheduleSingleReload(STYLESHEET_RELOAD_MARKER_KEY, 'base-stylesheet-missing');
        });

        return () => {
          if (typeof frameHandle === 'number') {
            globalThis.window?.cancelAnimationFrame(frameHandle);
          }
        };
      }, []);

      return <App />;
    }

    root.render(
      <React.StrictMode>
        <Root />
      </React.StrictMode>,
    );
  } catch (error) {
    const code = error instanceof FrontendRuntimeConfigError ? error.publicCode : 'RUNTIME_CONFIG_BOOTSTRAP_FAILED';
    console.error(`Frontend bootstrap blocked: ${code}`);
    renderDeploymentError(code);
  }
})();
